import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@shared';
import { CONSUMER_SELF_FUND_ACCESS_MONTHS, CONSUMER_SELF_FUND_PRICE_IN_CENTS } from '@shared/consumerPlan';
import { selectSession } from '@/model';
import { Checklist, type ChecklistItem } from '../components/checklist';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { selfFundCheckoutPath, selfFundPlanPath } from '../lib/paths';
import type { ConsumerStepProps } from './step-props';

const INCLUDED: ChecklistItem[] = [
  {
    marker: 'done',
    title: 'Project what you’ll need, from any account',
    description: 'Add savings, investments, retirement accounts, income or property, and see how they fund your care.',
  },
  { marker: 'done', title: 'Your care cost breakdown', description: 'Where care happens and what each phase is likely to cost.' },
  { marker: 'done', title: 'How family is predicted to step in', description: 'The care your family could realistically give, and what it saves.' },
  { marker: 'done', title: 'A PDF of your full plan', description: 'Download it to keep or share with family.' },
  {
    marker: 'done',
    title: 'A complimentary 15-minute planning session',
    description: 'Talk your plan through with one of our LTC Planning Experts.',
  },
];

export function SelfFundStep({ clientId }: ConsumerStepProps) {
  const navigate = useNavigate();
  const { finish } = useConsumerFlow(clientId);
  const hasUnlocked = useSelector(selectSession).clientStripe?.hasActivePlan ?? false;
  const price = formatCurrency(CONSUMER_SELF_FUND_PRICE_IN_CENTS / 100, 2);

  return (
    <StepFrame
      step="self-fund"
      title="Want to self-fund the entire care event?"
      subtitle="Unlock a self-guided flow to build a comprehensive, personalized self-funding plan."
      onBack={() => finish('options')}
      help={
        <>
          <p>
            This is a one-time {price} payment for {CONSUMER_SELF_FUND_ACCESS_MONTHS} months of access. There’s no
            automatic renewal and no recurring charge.
          </p>
          <p>After you unlock, you can book your planning session any time from the top of the page.</p>
          <p>Insurance options stay available: the plan can show you more efficient ways to protect your savings.</p>
        </>
      }
      primary={
        hasUnlocked
          ? { label: 'Continue my self-funding plan', onClick: () => navigate(selfFundPlanPath(clientId)) }
          : { label: 'Coming Soon', onClick: () => navigate(selfFundCheckoutPath(clientId)), disabled: true }
      }
      next={hasUnlocked ? 'your care costs, phase by phase.' : 'pay securely, then start planning.'}
    >
      <Checklist items={INCLUDED} />
    </StepFrame>
  );
}
